import { ErrorBoundary } from "react-error-boundary";
import { Redirect, Route as WouterRoute, Switch } from "wouter";
import { PageErrorFallback } from "@/components/ErrorFallback";
import { useAtomValue, useSetAtom } from "jotai";
import { contentRefreshKeyAtom, pageErrorStatusAtom, useSetPageTitle } from "@/hooks/pages";
import { navigate as setLocation } from 'wouter/use-browser-location';
import { useAdminPerms } from "@/hooks/auth";
import { lazy, Suspense } from "react";
import GenericSpinner from "@/components/GenericSpinner";

import Iframe from "@/pages/Iframe";
import NotFound from "@/pages/NotFound";
import UnauthorizedPage from "@/pages/UnauthorizedPage";
import {
    BanIcon,
    HeadphonesIcon,
    ShieldQuestionIcon,
} from "lucide-react";

const DashboardPage = lazy(() => import('@/pages/Dashboard/DashboardPage'));
const PerformancePage = lazy(() => import('@/pages/Dashboard/PerformancePage'));
const LiveConsolePage = lazy(() => import('@/pages/LiveConsole/LiveConsolePage'));
const TestingPage = lazy(() => import('@/pages/TestingPage/TestingPage'));
const PlayersPage = lazy(() => import('@/pages/Players/PlayersPage'));
const HistoryPage = lazy(() => import('@/pages/History/HistoryPage'));
const SystemLogPage = lazy(() => import('@/pages/SystemLogPage'));
const SchedulerPage = lazy(() => import('@/pages/SchedulerPage'));
const PlayerDropsPage = lazy(() => import('@/pages/PlayerDropsPage/PlayerDropsPage'));
const SettingsPage = lazy(() => import('@/pages/Settings/SettingsPage'));
const DiagnosticsPage = lazy(() => import('@/pages/Diagnostics/DiagnosticsPage'));
const AdvancedPage = lazy(() => import('@/pages/AdvancedPage'));
const FcAdminOverviewPage = lazy(() => import('@/pages/FcAdmin/FcAdminOverviewPage'));
const FcAdminDataPage = lazy(() => import('@/pages/FcAdmin/FcAdminDataPage'));
const FcAdminModerationPage = lazy(() => import('@/pages/FcAdmin/FcAdminModerationPage'));
const FcAdminPermissionsPage = lazy(() => import('@/pages/FcAdmin/FcAdminPermissionsPage'));
const FcAdminTeamPage = lazy(() => import('@/pages/FcAdmin/FcAdminTeamPage'));
const FcAdminPlayersPage = lazy(() => import('@/pages/FcAdmin/FcAdminPlayersPage'));
const FcAdminPlayerIdsPage = lazy(() => import('@/pages/FcAdmin/FcAdminPlayerIdsPage'));
const FcAdminPlaytimePage = lazy(() => import('@/pages/FcAdmin/FcAdminPlaytimePage'));
const FcAdminActionsPage = lazy(() => import('@/pages/FcAdmin/FcAdminActionsPage'));
const FcAdminAuditPage = lazy(() => import('@/pages/FcAdmin/FcAdminAuditPage'));
const NativeResourcesPage = lazy(() => import('@/pages/Native/NativePages').then((module) => ({ default: module.NativeResourcesPage })));
const NativeServerLogPage = lazy(() => import('@/pages/Native/NativePages').then((module) => ({ default: module.NativeServerLogPage })));
const NativeCfgEditorPage = lazy(() => import('@/pages/Native/NativePages').then((module) => ({ default: module.NativeCfgEditorPage })));
const NativeAllowlistPage = lazy(() => import('@/pages/Native/NativePages').then((module) => ({ default: module.NativeAllowlistPage })));
const NativeAdminsPage = lazy(() => import('@/pages/Native/NativePages').then((module) => ({ default: module.NativeAdminsPage })));
const NativeMasterActionsPage = lazy(() => import('@/pages/Native/NativePages').then((module) => ({ default: module.NativeMasterActionsPage })));


type RouteType = {
    path: string;
    title: string;
    permission?: string;
    Page: JSX.Element;
};

const allRoutes: RouteType[] = [
    {
        path: '/fc',
        title: 'FC Admin · Übersicht',
        Page: <FcAdminOverviewPage />
    },
    {
        path: '/fc/players',
        title: 'FC Admin · Spieler',
        Page: <FcAdminPlayersPage />
    },
    {
        path: '/fc/moderation',
        title: 'FC Admin · Bans & Warns',
        Page: <FcAdminModerationPage />
    },
    {
        path: '/fc/jail',
        title: 'FC Admin · Admin Jail',
        Page: <FcAdminDataPage title="Admin Jail" description="Aktive und abgeschlossene Haftfälle sowie Vorlagen." operation="jail.list" icon={<BanIcon />} />
    },
    {
        path: '/fc/support',
        title: 'FC Admin · Support',
        Page: <FcAdminDataPage title="Support" description="Supportfälle, Zuständigkeiten, Eskalationen und Verlauf." operation="support.list" icon={<HeadphonesIcon />} />
    },
    {
        path: '/fc/entry',
        title: 'FC Admin · Einreise',
        Page: <FcAdminDataPage title="Einreise" description="Offene und abgeschlossene Einreiseverfahren." operation="entry.list" icon={<ShieldQuestionIcon />} />
    },
    {
        path: '/fc/playtime',
        title: 'FC Admin · Spielzeit',
        Page: <FcAdminPlaytimePage />
    },
    {
        path: '/fc/player-ids',
        title: 'FC Admin · Spieler-IDs',
        Page: <FcAdminPlayerIdsPage />
    },
    {
        path: '/fc/team',
        title: 'FC Admin · Team',
        Page: <FcAdminTeamPage />
    },
    {
        path: '/fc/permissions',
        title: 'FC Admin · Rechtematrix',
        Page: <FcAdminPermissionsPage />
    },
    {
        path: '/fc/vip',
        title: 'FC Admin · Spieler',
        Page: <Redirect to="/fc/players" replace />
    },
    {
        path: '/fc/licenses',
        title: 'FC Admin · Spieler',
        Page: <Redirect to="/fc/players" replace />
    },
    {
        path: '/fc/economy',
        title: 'FC Admin · Spieler',
        Page: <Redirect to="/fc/players" replace />
    },
    {
        path: '/fc/vehicles',
        title: 'FC Admin · Spieler',
        Page: <Redirect to="/fc/players" replace />
    },
    {
        path: '/fc/actions',
        title: 'FC Admin · World Settings',
        Page: <FcAdminActionsPage />
    },
    {
        path: '/fc/audit',
        title: 'FC Admin · Logzentrale',
        Page: <FcAdminAuditPage />
    },
    {
        path: '/players',
        title: 'txAdmin · Spieler',
        Page: <PlayersPage />
    },
    {
        path: '/history',
        title: 'Verlauf',
        Page: <HistoryPage />
    },
    {
        path: '/insights/player-drops',
        title: 'Player Drops',
        Page: <PlayerDropsPage />
    },
    {
        path: '/allowlist',
        title: 'Allowlist',
        permission: 'players.whitelist',
        Page: <NativeAllowlistPage />
    },
    {
        path: '/admins',
        title: 'txAdmin-Konten',
        permission: 'manage.admins',
        Page: <NativeAdminsPage />
    },
    {
        path: '/settings',
        title: 'Einstellungen',
        permission: 'settings.view',
        Page: <SettingsPage />
    },
    {
        path: '/system/master-actions',
        title: 'Master Actions',
        permission: 'master',
        Page: <NativeMasterActionsPage />
    },
    {
        path: '/system/diagnostics',
        title: 'Diagnosen',
        Page: <DiagnosticsPage />
    },
    {
        path: '/system/console-log',
        title: 'Konsolenlog',
        permission: 'txadmin.log.view',
        Page: <SystemLogPage pageName="console" />
    },
    {
        path: '/system/action-log',
        title: 'Aktionslog',
        permission: 'txadmin.log.view',
        Page: <SystemLogPage pageName="action" />
    },

    {
        path: '/',
        title: 'FC Admin · Dashboard',
        Page: <DashboardPage />
    },
    {
        path: '/server/console',
        title: 'Live-Konsole',
        permission: 'console.view',
        Page: <LiveConsolePage />
    },
    {
        path: '/server/resources',
        title: 'Resources',
        Page: <NativeResourcesPage />
    },
    {
        path: '/server/performance',
        title: 'Serverbetrieb · Performance',
        Page: <PerformancePage />
    },
    {
        path: '/server/scheduler',
        title: 'Serverbetrieb · Scheduler',
        Page: <SchedulerPage />
    },
    {
        path: '/server/server-log',
        title: 'Server Log',
        permission: 'server.log.view',
        Page: <NativeServerLogPage />
    },
    {
        path: '/server/cfg-editor',
        title: 'CFG Editor',
        permission: 'server.cfg.editor',
        Page: <NativeCfgEditorPage />
    },
    {
        path: '/server/setup',
        title: 'Server-Einrichtung',
        permission: 'master',
        Page: <Iframe legacyUrl="setup" />
    },
    {
        path: '/server/deployer',
        title: 'Server-Bereitstellung',
        permission: 'master',
        Page: <Iframe legacyUrl="deployer" />
    },
    {
        path: '/advanced',
        title: 'Erweitert',
        permission: 'all_permissions',
        Page: <AdvancedPage />
    },

    {
        path: '/settings/ban-templates',
        title: 'Ban-Vorlagen',
        Page: <FcAdminModerationPage />
    },
    {
        path: '/ban-identifiers',
        title: 'Ban-Identifier',
        Page: <FcAdminModerationPage />
    },
];


function Route(route: RouteType) {
    const { hasPerm } = useAdminPerms();
    const setPageTitle = useSetPageTitle();
    setPageTitle(route.title);
    const nodeToRender = route.permission && !hasPerm(route.permission)
        ? <UnauthorizedPage pageName={route.title} permission={route.permission} />
        : route.Page;
    return <WouterRoute path={route.path}>{nodeToRender}</WouterRoute>
}


export function MainRouterInner() {
    return (
        <Suspense fallback={<div className="flex size-full items-center justify-center"><GenericSpinner msg="FC Admin wird geladen …" /></div>}>
            <Switch>
                {allRoutes.map((route) => <Route key={route.path} {...route} />)}

                {import.meta.env.DEV && (
                    <WouterRoute path="/test"><TestingPage /></WouterRoute>
                )}
                <WouterRoute component={NotFound} />
            </Switch>
        </Suspense>
    );
}


export default function MainRouter() {
    const setPageErrorStatus = useSetAtom(pageErrorStatusAtom);
    const contentRefreshKey = useAtomValue(contentRefreshKeyAtom);

    return (
        <ErrorBoundary
            key={contentRefreshKey}
            FallbackComponent={PageErrorFallback}
            onError={() => {
                console.log('Page ErrorBoundary caught an error');
                setPageErrorStatus(true);
            }}
            onReset={() => {
                console.log('Page ErrorBoundary reset');
                setLocation('/');
                setPageErrorStatus(false);
            }}
        >
            <MainRouterInner />
        </ErrorBoundary>
    );
}
